/* eslint-disable */
import * as _ from "lodash";
import { Injectable, inject } from "@angular/core";
import { LgTranslateService } from "@logex/framework/lg-localization";
import * as Filters from "@logex/framework/lg-filterset";

import {
    FilterFactoryCreatorBase,
    IFilterFactoryEntry,
    MapFilterDefinition,
    MapFilterStore,
    SupportedFilters
} from "./bases/filter-factory-base";

// import { AppDefinitions, DefinitionKey } from "./app-definitions.service";
// import { AppVersion } from "./app-version";
import { IItemClusterFilterDefinition } from "@logex/framework/ui-toolbox";
import { DefinitionsService } from "./services/definitions.service";

// ---------------------------------------------------------------------------------------------
//  Implementation of the FilterFactory service
// ---------------------------------------------------------------------------------------------

@Injectable()
export class FilterFactory {
    private _definitions = inject(DefinitionsService);
    private _filterSetService = inject(Filters.LgFilterSetService);
    private _lgTranslateService = inject(LgTranslateService);

    define(): FilterFactoryCreator {
        return new FilterFactoryCreator(
            this._filterSetService,
            this._lgTranslateService,
            this._definitions
        );
    }
}

// ---------------------------------------------------------------------------------------------
type DateFilterParameters = Pick<
    Filters.IDateFilterDefinition,
    "min" | "max" | "default" | "nameLC" | "initialMaxValue" | "initialMinValue" | "visible"
>;

type Combo2Parameters<T> = Pick<
    Filters.IComboFilter2Definition<T>,
    "source" | "onBecameActive" | "onBecameActiveLimit" | "onChanged" | "visible" | "nameLC"
>;

type SelectableCombo2Parameters<T> = Pick<
    Filters.ISelectableComboFilter2Definition<T>,
    | "source"
    | "onBecameActive"
    | "onBecameActiveLimit"
    | "onChanged"
    | "visible"
    | "nameLC"
    | "onSelectionChanged"
    | "onSelectionRemoved"
>;

type CheckboxParameters = Pick<
    Filters.ICheckboxFilterDefinition,
    | "uncheckedFilters"
    | "default"
    | "previewTextLC"
    | "previewPopup"
    | "nameLC"
    | "onChanged"
    | "visible"
>;

type InputRangeParameters = Pick<
    Filters.IInputRangeFilterDefinition,
    "min" | "max" | "format" | "decimals" | "default" | "onChanged" | "visible" | "nameLC"
>;

type RangerFilterParameters = Partial<
    Pick<
        Filters.IRangeFilterDefinition,
        | "checkboxVisible"
        | "min"
        | "minusInfinity"
        | "max"
        | "plusInfinity"
        | "tickerStep"
        | "highlightStep"
        | "keyboardStep"
        | "minusInfinityText"
        | "plusInfinityText"
        | "default"
        | "onChanged"
        | "visible"
        | "nameLC"
    >
>;

// type DecodeComboType<T extends "number" | "string"> = T extends "number" ? number : T extends "string" ? string : never;

type CareProductClusterFilterParameters = Pick<
    IItemClusterFilterDefinition<number>,
    "source" | "onBecameActive" | "onBecameActiveLimit" | "onChanged" | "visible" | "nameLC"
> & { getVisibleProducts?: null | (() => number[]) };

// ---------------------------------------------------------------------------------------------
//  Implementation of the creator.
// ---------------------------------------------------------------------------------------------
export class FilterFactoryCreator<
    Definitions extends _.Dictionary<Filters.INormalizedFilterDefinition> = {},
    Filters extends Filters.IFilterList = {}
> extends FilterFactoryCreatorBase {
    constructor(
        _filterSetService: Filters.LgFilterSetService,
        _lgTranslateService: LgTranslateService,
        private _definitions: DefinitionsService
    ) {
        super(_filterSetService, _lgTranslateService);
    }

    // ---------------------------------------------------------------------------------------------
    addFilter<T extends SupportedFilters, N extends string>(
        id: N,
        params: T
    ): FilterFactoryCreator<
        Definitions & {
            [P in N]: MapFilterDefinition<T>;
        },
        Filters & {
            [P in N]: MapFilterStore<T>;
        }
    > {
        return this._addFilter(id, params);
    }

    // ---------------------------------------------------------------------------------------------
    create(context: any): Filters.LgFilterSet<Definitions, Filters> {
        return this._create<Definitions, Filters>(context);
    }

    // MY FILTERS
    // ---------------------------------------------------------------------------------------------

    professionalFilter<T extends string>(id: T, params: Combo2Parameters<string>) {
        return this.createCombo2String(id, params, {
            nameLC: "APP._Filters.Professional",
            mapToOptions: this.createMapper<string>("professional", true)
        });
    }

    agbCodeFilter<T extends string>(id: T, params: Combo2Parameters<string>) {
        return this.createCombo2String(id, params, {
            nameLC: "APP._Filters.AgbCode",
            mapToOptions: this.createMapper<string>("agbCode", true)
        });
    }

    private createMapper<T extends number | string>(id: string, sortById: boolean) {
        return (codes: T[]) =>
            _(codes)
                .map(code => {
                    // const name = this._definitions.getDisplayName(id, code)
                    return {
                        id: code,
                        order: sortById ? code : name,
                        name: code.toString()
                    };
                })
                .sortBy(f => f.name)
                .value();
    }

    // ---------------------------------------------------------------------------------------------
    addMappedNumberCombo2Filter<T extends string>(
        id: T,
        params: Combo2Parameters<number> & {
            optionName: (id: number) => string;
            optionOrderById?: boolean;
        }
    ) {
        return this.createCombo2Number(id, params, {
            mapToOptions: ids =>
                _(ids)
                    .map(id => {
                        const name = params.optionName(id);
                        return {
                            id,
                            name,
                            sort: params.optionOrderById ? id : name
                        };
                    })
                    .sortBy(f => f.sort)
                    .value()
        });
    }

    // ---------------------------------------------------------------------------------------------
    addMappedStringCombo2Filter<T extends string>(
        id: T,
        params: Combo2Parameters<string> & {
            optionName: (id: string) => string;
            optionOrderById?: boolean;
        }
    ) {
        return this.createCombo2String(id, params, {
            mapToOptions: ids =>
                _(ids)
                    .map(id => {
                        const name = params.optionName(id);
                        return {
                            id,
                            name,
                            sort: params.optionOrderById ? id : name
                        };
                    })
                    .sortBy(f => f.sort)
                    .value()
        });
    }

    // ---------------------------------------------------------------------------------------------
    addCheckboxFilter<T extends string>(id: T, params: CheckboxParameters) {
        return this.createCheckbox(id, params, null);
    }

    // ---------------------------------------------------------------------------------------------
    addErrorFilter<T extends string>(id: T, params: CheckboxParameters) {
        return this.createCheckbox(id, params, {
            nameLC: "APP._Filters.Errors"
        });
    }

    // ---------------------------------------------------------------------------------------------
    //  Private helpers
    // ---------------------------------------------------------------------------------------------
    private createCombo2Number<T extends string>(
        id: T,
        params: Combo2Parameters<number>,
        defaults: Partial<IFilterFactoryEntry<Filters.IComboFilter2Definition<number>>>
    ) {
        return this.addFilter(id, {
            filterType: "combo2",
            main: true,
            idType: "number",
            visible: () => true,
            ...defaults,
            ...params
        } as IFilterFactoryEntry<Filters.IComboFilter2Definition<number>>);
    }

    // ---------------------------------------------------------------------------------------------
    private createCombo2String<T extends string>(
        id: T,
        params: Combo2Parameters<string>,
        defaults: Partial<IFilterFactoryEntry<Filters.IComboFilter2Definition<string>>>
    ) {
        return this.addFilter(id, {
            filterType: "combo2",
            main: true,
            idType: "string",
            visible: () => true,
            ...defaults,
            ...params
        } as IFilterFactoryEntry<Filters.IComboFilter2Definition<string>>);
    }

    private createInputRange<T extends string>(
        id: T,
        params: InputRangeParameters,
        defaults: Partial<InputRangeParameters>
    ) {
        return this.addFilter(id, {
            filterType: "inputRange",
            main: true,
            visible: () => true,
            ...defaults,
            ...params
        } as IFilterFactoryEntry<Filters.IInputRangeFilterDefinition>);
    }

    // ---------------------------------------------------------------------------------------------
    private createCheckbox<T extends string>(
        id: T,
        params: CheckboxParameters,
        defaults: Partial<IFilterFactoryEntry<Filters.ICheckboxFilterDefinition>>
    ) {
        return this.addFilter(id, {
            filterType: "checkbox",
            main: true,
            visible: () => true,
            ...defaults,
            ...params
        } as IFilterFactoryEntry<Filters.ICheckboxFilterDefinition>);
    }

    // ---------------------------------------------------------------------------------------------
}
